import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm, NgModel } from '@angular/forms';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { EmailOrUserDirective, LoadingIndicatorDirective } from '@zero/shared/ui-directives';
import { LoginInfoCardsComponent, SlimFooterComponent, UserMessagesComponent, UserNotesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { MaintenanceWorkFacade } from '@zero/shared/util-global-state';
import { IBackendResponse, UserMessage, ValidationPatterns } from '@zero/shared/util-models';
import { AuthService, RouteOptionsService, TrackingService, UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

interface IConfirmationData {
  email: string;
  code: string;
  adAgreed: boolean;
}

@Component({
  templateUrl: 'login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    CommonModule,
    TranslocoPipe,
    RouterLink,
    LoginInfoCardsComponent,
    LoadingIndicatorDirective,
    EmailOrUserDirective,
    SlimFooterComponent,
    UserNotesComponent,
    UserMessagesComponent,
    FormsModule,
    ButtonComponent,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  public password: string;
  public userName: string;
  public passwordPattern: RegExp = ValidationPatterns.passwordPattern;
  public commonRoutes = commonRoutes;
  public validatedAtLeastOnce = false;
  public ongoingRequest = false;
  public signupConfirmation = false;
  public confirmationState = '';
  public confirmationErrorMessage = '';

  private _subscriptions: Subscription = new Subscription();
  private _confirmationData: IConfirmationData = {
    email: undefined,
    code: undefined,
    adAgreed: false,
  };

  @ViewChild('userNameVar') public userNameInputRef: NgModel;

  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _messageService = inject(UserMessagesHolderService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _trackingService = inject(TrackingService);
  private readonly _routeOptionsService = inject(RouteOptionsService);
  public readonly maintenanceService = inject(MaintenanceWorkFacade);

  ngOnInit(): void {
    this._routeOptionsService.hideHeaderAndFooter();
    if (this._router.url.startsWith(commonRoutes.prelogin.emailregistration)) {
      this.signupConfirmation = true;
    }
    this.listenToQueryParams();
    this.listenToLoggedInState();
    this.listenToMaintenanceMode();
    this.#toggleBg();
  }

  ngOnDestroy(): void {
    this._routeOptionsService.displayHeaderAndFooter();
    this._subscriptions.unsubscribe();
    this.#toggleBg();
  }

  #toggleBg(): void {
    const { classList } = document.body;
    const cssClass = 'main-dark-blue';
    classList.contains(cssClass) ? classList.remove(cssClass) : classList.add(cssClass);
  }

  public login(loginForm: NgForm): void {
    this.validatedAtLeastOnce = true;
    this._messageService.clearAllMessages();
    if (loginForm?.valid) {
      this.handleValidLoginForm();
    } else {
      const passwordInvalid = loginForm?.controls['password']?.invalid;
      const userNameInvalid = loginForm?.controls['userName']?.invalid;
      if (passwordInvalid && userNameInvalid) {
        this._messageService.addMessage(
          new UserMessage('Dein eingegebender Benutzername und Dein eingegebenes Passwort entsprechen nicht den Anforderungen.', 'danger'),
        );
      } else if (passwordInvalid) {
        this._messageService.addMessage(new UserMessage('Dein eingegebenes Passwort entspricht nicht den Anforderungen.', 'danger'));
      } else {
        this._messageService.addMessage(new UserMessage('Dein eingegebender Benutzername entspricht nicht den Anforderungen.', 'danger'));
      }
    }
  }

  private handleValidLoginForm = (): void => {
    this.ongoingRequest = true;
    this._subscriptions.add(
      this._authService.login(this.userName, this.password).subscribe((result: IBackendResponse<any>) => {
        this.ongoingRequest = false;
        if (!result.success) {
          if (result.message) {
            this._messageService.addMessage(new UserMessage(result.message, 'danger', true));
          }
          return;
        } else if (result.message) {
          this._messageService.clearAllMessages();
          this._messageService.addSuccessMessage(result.message);
        }

        this.redirectAfterLogin();
      }),
    );
  };

  public isNewPasswordRequired(): boolean {
    return this._authService.isNewPasswordRequired();
  }

  public setNewPassword(newPWForm: NgForm): void {
    this._messageService.clearAllMessages();
    this._authService.setNewPassword(newPWForm.value.inputNewPassword).subscribe((result: IBackendResponse<any>) => {
      if (result.success) {
        this._router.navigate([commonRoutes.prelogin.home]);
        if (result.message) {
          this.password = undefined;
          this._messageService.addMessage(new UserMessage(result.message, 'info', false), true);
        }
      } else {
        if (result.message) {
          this._messageService.addErrorMessage(result.message);
        }
      }
    });
  }

  private redirectAfterLogin(): void {
    this._authService.redirectAfterLogin();
  }

  private sendConfirmationData(email: string, code: string, adAgreed: boolean): void {
    this._subscriptions.add(
      this._authService.sendConfirmationDataForEmailConfirmation(email, code, adAgreed).subscribe({
        next: (isSuccessful) => {
          if (isSuccessful) {
            this.confirmationState = 'valid';
            if (this._confirmationData?.email && this.userNameInputRef) {
              this.userNameInputRef.control.setValue(this._confirmationData.email);
              this.userNameInputRef.control.markAsDirty();
            }
            this._trackingService.trackEvent('reg_doi', 'register', 'component');
          } else {
            this.confirmationState = 'invalid';
          }
        },
        error: (e) => {
          if (e?.status === 406) {
            this.confirmationState = 'not-acceptable';
            this.confirmationErrorMessage = e?.error?.message;
          } else {
            this.confirmationState = 'invalid';
          }
        },
      }),
    );
  }

  private listenToQueryParams = (): void => {
    this._route.queryParams.subscribe((par: Params) => {
      if (this.signupConfirmation) {
        this._confirmationData.email = par['email'];
        this._confirmationData.code = par['code'];
        this._confirmationData.adAgreed = par['adAgreed'] !== '' && String(par['adAgreed']).toLowerCase() === 'true';

        if (this._confirmationData.email && this._confirmationData.code) {
          this.sendConfirmationData(this._confirmationData.email, this._confirmationData.code, this._confirmationData.adAgreed);
        } else {
          this.confirmationState = 'invalid';
        }
      } else if (par['confirmedEmail']) {
        this.userNameInputRef.control.setValue(par['confirmedEmail']);
      }
    });
  };

  private listenToLoggedInState = (): void => {
    this._subscriptions.add(
      this._authService
        .isLoggedIn$()
        .pipe(take(1))
        .subscribe({
          next: (loggedIn) => {
            this.ongoingRequest = true;
            if (loggedIn) {
              if (this.signupConfirmation) {
                this._authService.logout(false, false, undefined, {
                  reason: 'LoginComponent.ngOnInit: loggedIn and signupConfirmation=true',
                });
              } else {
                this._messageService.clearAllMessages();
                this.redirectAfterLogin();
              }
            }
            this.ongoingRequest = false;
          },
        }),
    );
  };

  private listenToMaintenanceMode = (): void =>
    this._subscriptions.add(
      this.maintenanceService.statusMessage$.subscribe((msg) =>
        msg ? this._messageService.addMessage(new UserMessage(msg, 'danger')) : this._messageService.clearAllMessages(),
      ),
    );
}
